'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface TrailNode {
  id: string;
  type: 'letter' | 'garden' | 'irl' | 'mrg';
  label: string;
  arabic?: string;
  phrase: string;
  description: string;
  href: string;
  color: string;
  icon: string;
  vernacularNote?: string;
}

interface TrailStep {
  letterSection: string;
  letterSectionId: string;
  letterPhrase: string;
  letterContext: string;
  nodes: TrailNode[];
  barzakhNote?: string;
}

// ─── TRAIL DATA ───────────────────────────────────────────────────────────────

const TRAIL_STEPS: TrailStep[] = [
  {
    letterSection: 'Section 1 — The Recognition',
    letterSectionId: 'opening',
    letterPhrase: '"Go from aware to conscious, then act accordingly."',
    letterContext: 'The arc of the entire platform in one sentence. Aware → Conscious → Acting.',
    barzakhNote: 'You cross from letter into garden without noticing. The ground changes beneath you.',
    nodes: [
      {
        id: 'keys-ayat',
        type: 'garden',
        label: 'Keys to Kulli Haal',
        arabic: 'مَفَاتِيح',
        phrase: 'Key 01: Ayat — the signs',
        description: 'The Recognition section plants the first key. Ayat are the signs in creation and revelation — the curriculum begins here.',
        href: '/keys-to-kulli-haal',
        color: '#D4A82A',
        icon: '⌘',
        vernacularNote: 'The signs surround us. We still in the aisles.',
      },
      {
        id: 'irl-encounter',
        type: 'irl',
        label: 'IRL Lens — Encounter',
        arabic: 'لِقَاء',
        phrase: 'Mata → first encounter',
        description: 'The IRL Lens begins with Mata — the first encounter. "We aren\'t some spontaneous occurrence" is the lens\'s opening premise.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'What do you sense right now? Before anything else.',
      },
      {
        id: 'mrg-mizan',
        type: 'mrg',
        label: 'Muslim Resource Guide',
        arabic: 'الْمِيزَان',
        phrase: 'Mizan — the balance not to transgress',
        description: 'The MRG is where the Mizan becomes visible in ward data. "A purposeful balance" — the MRG shows where it has been disrupted.',
        href: '/muslim-resource-guide',
        color: '#2d7a47',
        icon: '⬡',
        vernacularNote: 'Somebody\'s hungry in your neighborhood. What you gonna do about it?',
      },
    ],
  },
  {
    letterSection: 'Section 2 — Caretaker',
    letterSectionId: 'caretaker',
    letterPhrase: '"Life starts at the checkout, we still in the aisles."',
    letterContext: 'The code-switch. The register shifts. The dunyah is the store. We\'re gathering provisions. The checkout is the crossing point.',
    barzakhNote: 'The formal register and the vernacular register are both the Haal Project. Neither is decoration.',
    nodes: [
      {
        id: 'garden-fitra',
        type: 'garden',
        label: 'My Garden — Fitra Bedrock',
        arabic: 'فِطْرَة',
        phrase: '"No change in the creation of Allah" — Quran 30:30',
        description: 'The Fitra module is indestructible. Before any module is planted, the original design is acknowledged. The garden begins with what was already there.',
        href: '/user-dashboard',
        color: '#c9a84c',
        icon: '⬡',
        vernacularNote: 'You were made for this. The checkout is coming. What\'s in your cart?',
      },
      {
        id: 'irl-hawas',
        type: 'irl',
        label: 'IRL Lens — Hawas Calibration',
        arabic: 'حَوَاس',
        phrase: '"The curriculum begins with the sense(s)"',
        description: 'The IRL Lens opens with sensory calibration — Hawas. What do you see, hear, smell, touch? The curriculum begins here, not with theology.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'What do you sense right now? Be real about it.',
      },
      {
        id: 'garden-garment',
        type: 'garden',
        label: 'My Garden — Garment',
        arabic: 'رِشَاطُ التَّقْوَى',
        phrase: '"These bodies are the garments we wear, the seeds we plant."',
        description: 'The Garment and the Garden are the same journey viewed from two angles. What you cultivate (garden/seeds) is what you become (garment/body).',
        href: '/user-dashboard?tab=garment',
        color: '#8a8ab8',
        icon: '⬡',
        vernacularNote: 'You wear what you grow. You grow what you wear.',
      },
    ],
  },
  {
    letterSection: 'Section 3 — The Mural',
    letterSectionId: 'the-mural',
    letterPhrase: '"A path through the nearest emerges as a multi-layered forest watershed from highlands to valleys, canopy to roots."',
    letterContext: 'The Mural opens nearest-first. Not the most dramatic biome — the nearest. The path moves from visible to hidden, Zahir to Batin.',
    barzakhNote: 'The boundary between land and water dissolved. You\'re walking on water before you know it.',
    nodes: [
      {
        id: 'irl-three-questions',
        type: 'irl',
        label: 'IRL Lens — Three Questions',
        arabic: 'ثَلَاثَة أَسْئِلَة',
        phrase: 'Ilm Yaqeen → Ayn Yaqeen → Haqq Yaqeen',
        description: 'The Three Stations of Certainty from the Mural section map directly to the IRL Lens\'s three-question framework. Knowledge → Perception → Reality.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'Is seeing believing? Come here and experience it in person.',
      },
      {
        id: 'ayat-creation',
        type: 'garden',
        label: 'Ayat in Creation',
        arabic: 'آيَاتٌ فِي الْآفَاق',
        phrase: '"Surely Signs for a people who use their intellect" — Quran 2:164',
        description: 'The Mural\'s Quranic verses (2:164, 27:60, 15:19-23) are the theological ground of Ayat in Creation. The signs in the horizons and in yourselves.',
        href: '/ayat-in-creation',
        color: '#2d7a47',
        icon: '☽',
        vernacularNote: 'The signs surround us. Look up from the screen.',
      },
      {
        id: 'mrg-hikmah',
        type: 'mrg',
        label: 'MRG — Hikmah Station',
        arabic: 'حِكْمَة',
        phrase: '"Gathered information highlighting connections, processes, and diagrams unfolding how resources flow"',
        description: 'The Hikmah Station in the Mural is the ancestor of every MRG resource cluster. One clearing where wisdom gathers — you reach it by following the nearest path.',
        href: '/muslim-resource-guide',
        color: '#2d7a47',
        icon: '⬡',
        vernacularNote: 'Resources flow through habitats and shared spaces. Who\'s tending the flow in your ward?',
      },
    ],
  },
  {
    letterSection: "Section 3.5 — Roots Ma'rifah",
    letterSectionId: 'roots-marifah',
    letterPhrase: '"Soon, We will show them Our Ayat in the horizons and in themselves until becomes clear to them that it\'s the truth." — Quran 41:53',
    letterContext: "The theological foundation of the IRL Lens itself. Roots Ma'rifah maps the dual Ayat — signs in creation and signs in revelation — as twin trusts upon the caretaker.",
    barzakhNote: "Ma'rifah — awareness that comes from encounter. You don't study your way to it. You walk your way to it.",
    nodes: [
      {
        id: 'irl-dual-ayat',
        type: 'irl',
        label: 'IRL Lens — Dual Ayat',
        arabic: 'آيَاتٌ وَآيَاتٌ',
        phrase: 'Signs in creation + Signs in revelation — simultaneously',
        description: "Quran 41:53 is the generative verse of the IRL Lens. The Roots Ma'rifah section establishes that Ayat in creation and Ayat in revelation are both trusts — the IRL Lens reads both simultaneously, not sequentially.",
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: "The senses we're given allow us to perceive Ayat. Our sense allows us to receive Ayat. Both. At once.",
      },
      {
        id: 'keys-fitrah',
        type: 'garden',
        label: 'Keys — Fitrah as Bedrock',
        arabic: 'فِطْرَة',
        phrase: '"Our Fitrah, Original Design, upon which we were created"',
        description: "The Roots section grounds Fitrah not as a concept to study but as the original design already in us. Key 01: Ayat begins with this recognition — you were designed to see what you're about to be shown.",
        href: '/keys-to-kulli-haal',
        color: '#D4A82A',
        icon: '⌘',
        vernacularNote: 'The Fitrah is intact. It gets covered over. The garden uncovers. It does not construct.',
      },
      {
        id: 'mrg-caretaker-roles',
        type: 'mrg',
        label: 'MRG — Caretaker Roles',
        arabic: 'أَدْوَار',
        phrase: '"An engineer, a teacher, a scientist, a student, a farmer, a native, a formerly incarcerated individual..."',
        description: "The Roots section names every role in society as a potential Khalifah role. The MRG operationalizes this — every resource domain (housing, food, education, health) is a domain of trust assigned to specific caretaker roles.",
        href: '/muslim-resource-guide',
        color: '#2d7a47',
        icon: '⬡',
        vernacularNote: "What role do you occupy? What would it look like if its primary purpose were oriented toward the remembrance of Allah?",
      },
    ],
  },
  {
    letterSection: 'Section 4 — Al-Qadir',
    letterSectionId: 'al-qadir',
    letterPhrase: '"The All-Powerful — the One whose power is complete, whose decree is absolute."',
    letterContext: 'Al-Qadir introduces the Names framework. Every Name is a lens for reading creation. The garden grows from these Names.',
    barzakhNote: 'The Names are not labels. They are the measurements every created thing carries.',
    nodes: [
      {
        id: 'names-reference',
        type: 'garden',
        label: 'Names Reference',
        arabic: 'الأَسْمَاء الحُسْنَى',
        phrase: 'Al-Qadir — the first Name extracted',
        description: 'The Names Reference is the living catalog of what the IRL Lens extracts. Al-Qadir is the first Name the letter analyzes — the garden\'s first harvest.',
        href: '/names-reference',
        color: '#8a8ab8',
        icon: '✦',
        vernacularNote: 'Every thing you see is wearing a Name. What Name is this wearing?',
      },
      {
        id: 'irl-fission',
        type: 'irl',
        label: 'IRL Lens — Fission Sequence',
        arabic: 'تَفْكِيك',
        phrase: 'Deconstruct → extract Names → identify Amanah',
        description: 'The fission sequence is the IRL Lens at full depth. The letter\'s analysis of Al-Qadir is a fission sequence performed in prose.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'Processing what I see, a bit of how I see it, and what I recognize on the way.',
      },
    ],
  },
  {
    letterSection: 'Section 5 — Al-Khabir · Al-Zahir',
    letterSectionId: 'al-khabir-al-zahir',
    letterPhrase: '"The batin — hidden is accessible and becomes zahir — manifest. Both are woven into the tapestry of existence."',
    letterContext: 'The Zahir/Batin axis is the IRL Lens\'s deepest architecture. Outer encounter and inner recognition are not separate — they are the same tapestry viewed from two sides.',
    barzakhNote: 'You perceive the zahir with basar — eyesight. You perceive the batin with basirah — insight. The lens pairs them.',
    nodes: [
      {
        id: 'irl-zahir-batin',
        type: 'irl',
        label: 'IRL Lens — Zahir / Batin Layers',
        arabic: 'الظَّاهِر · الْبَاطِن',
        phrase: '8-layer architecture — outer encounter to inner recognition',
        description: 'The IRL Lens\'s 8-layer pathway is the Zahir/Batin axis made interactive. Layer 1 (Mata) is the zahir encounter. Layers 6-8 (Aql, Fikr, Haqq) are the batin recognition. Both are woven together.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'What does it look like? What does it mean? Both questions. Same thing.',
      },
      {
        id: 'keys-furqan',
        type: 'garden',
        label: 'Keys — Furqan, The Discernment',
        arabic: 'الْفُرْقَان',
        phrase: '"We were also granted batin, hidden, inner furqan — that which makes things clear via insight"',
        description: 'The Keys curriculum is the Furqan — the discernment faculty made systematic. The Quran is named Al-Furqan because it distinguishes truth from falsehood. The Keys are how that discernment is practiced.',
        href: '/keys-to-kulli-haal',
        color: '#D4A82A',
        icon: '⌘',
        vernacularNote: 'Eyes for seeing. Heart for understanding. Intellect for pondering. All three. All at once.',
      },
      {
        id: 'mrg-batin-roots',
        type: 'mrg',
        label: 'MRG — Batin Roots of Disparity',
        arabic: 'الْجُذُور الْخَفِيَّة',
        phrase: '"Despite not being immediately evident, with applied effort, the batin becomes zahir"',
        description: 'The MRG\'s ward disparity data is the zahir of deeper batin causes — redlining, disinvestment, policy gaps. The Observation Chain Archive\'s Batin Depth filter surfaces these hidden structural roots.',
        href: '/muslim-resource-guide',
        color: '#2d7a47',
        icon: '⬡',
        vernacularNote: 'The ward ain\'t broken. The trust was violated. The data shows the violation.',
      },
    ],
  },
  {
    letterSection: 'Section 6 — Examination',
    letterSectionId: 'examination',
    letterPhrase: '"Understand the dimensions of a space, state, thing, its garment and grasping the measurements that it\'s given."',
    letterContext: 'The IRL Lens methodology in one breath. Space → State → Thing → Garment → Measurements. The entire 8-layer pathway compressed.',
    barzakhNote: 'You don\'t flip a switch from unaware to conscious. You walk, and at some point you realize the ground has changed.',
    nodes: [
      {
        id: 'irl-8layer',
        type: 'irl',
        label: 'IRL Lens — 8-Layer Pathway',
        arabic: 'الطَّرِيق',
        phrase: 'Mata → Encounter → Examination → Recognition → Re-Assembly → Uboor',
        description: 'The 8-layer recognition pathway is the letter\'s examination methodology made interactive. Each layer is a dimension of the space, state, or thing being examined.',
        href: '/inner-reality-lens',
        color: '#2d8a87',
        icon: '◎',
        vernacularNote: 'Understand the dimensions. Its garment. The measurements it\'s given.',
      },
      {
        id: 'garden-observation',
        type: 'garden',
        label: 'My Garden — Observation Chain',
        arabic: 'سِلْسِلَة',
        phrase: 'Sample examinations, commentary, and field notes',
        description: 'The Observation Chain Archive is the collective repository of field notes from all caretakers. The Open Letter is the first observation chain.',
        href: '/observation-chain-archive',
        color: '#c9a84c',
        icon: '◈',
        vernacularNote: 'Field notes are valid. Not everything must be fissioned. Sometimes documentation is enough.',
      },
      {
        id: 'mrg-amanah',
        type: 'mrg',
        label: 'MRG — Amanah Bridge',
        arabic: 'أَمَانَة',
        phrase: '"Builds toward environmental responsibility"',
        description: 'Every IRL Lens examination builds toward an Amanah — a responsibility. The MRG is where that responsibility becomes action. Recognition → Service.',
        href: '/muslim-resource-guide',
        color: '#2d7a47',
        icon: '⬡',
        vernacularNote: 'Aware of a good to do, encouraged to be conscious of how.',
      },
    ],
  },
];

// ─── NODE TYPE COLORS ─────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  letter: '#c9a84c',
  garden: '#2d7a47',
  irl: '#2d8a87',
  mrg: '#5a8a5a',
};

const TYPE_LABELS: Record<string, string> = {
  letter: 'Open Letter',
  garden: 'My Garden',
  irl: 'IRL Lens',
  mrg: 'MRG',
};

// ─── TRAIL NODE CARD ──────────────────────────────────────────────────────────

function TrailNodeCard({ node, index }: { node: TrailNode; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        background: `rgba(6,13,18,0.7)`,
        border: `1px solid ${node.color}30`,
        borderLeft: `3px solid ${node.color}`,
        borderRadius: '0 8px 8px 0',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          textAlign: 'left',
        }}
      >
        {/* Icon + type badge */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{
            width: 32, height: 32,
            borderRadius: '50%',
            background: `${node.color}15`,
            border: `1px solid ${node.color}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: node.color,
          }}>
            {node.icon}
          </div>
          <div style={{
            fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase',
            color: node.color, fontFamily: 'monospace',
            background: `${node.color}10`,
            border: `1px solid ${node.color}25`,
            borderRadius: 3, padding: '2px 5px',
            whiteSpace: 'nowrap',
          }}>
            {TYPE_LABELS[node.type]}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div>
              {node.arabic && (
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${node.color}80`, direction: 'rtl', marginBottom: 2 }}>
                  {node.arabic}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#d4c5a9', fontWeight: 600, marginBottom: 4 }}>
                {node.label}
              </div>
              <div style={{ fontSize: 11, color: '#9a8f7d', fontStyle: 'italic', lineHeight: 1.5 }}>
                {node.phrase}
              </div>
            </div>
            <div style={{
              color: node.color, fontSize: 14, flexShrink: 0,
              transform: expanded ? 'rotate(45deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}>+</div>
          </div>
        </div>
      </button>

      {/* Expanded */}
      {expanded && (
        <div style={{
          padding: '0 16px 16px 60px',
          borderTop: `1px solid ${node.color}15`,
          animation: 'trailExpand 0.2s ease',
        }}>
          <style>{`@keyframes trailExpand { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          <p style={{ fontSize: 12, color: '#9a8f7d', lineHeight: 1.7, marginTop: 12, marginBottom: 12 }}>
            {node.description}
          </p>
          {node.vernacularNote && (
            <div style={{
              background: 'rgba(201,168,76,0.04)',
              border: '1px solid rgba(201,168,76,0.12)',
              borderRadius: 6,
              padding: '10px 14px',
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                ◈ COMPANION VOICE
              </div>
              <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.7)', fontStyle: 'italic', lineHeight: 1.6 }}>
                "{node.vernacularNote}"
              </p>
            </div>
          )}
          <Link
            href={node.href}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 10, color: node.color, textDecoration: 'none',
              border: `1px solid ${node.color}30`,
              borderRadius: 4, padding: '6px 12px',
              background: `${node.color}08`,
              fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              transition: 'background 0.2s',
            }}
          >
            → Enter {TYPE_LABELS[node.type]}
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── BARZAKH CONNECTOR ────────────────────────────────────────────────────────

function BarzakhConnector({ note }: { note?: string }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0', position: 'relative' }}>
      {/* Vertical line */}
      <div style={{
        width: 1,
        height: 32,
        background: 'linear-gradient(180deg, rgba(201,168,76,0.3) 0%, rgba(201,168,76,0.05) 100%)',
      }} />
      {/* Barzakh node */}
      <button
        onClick={() => setRevealed(v => !v)}
        title="Barzakh — the dissolved boundary"
        style={{
          background: 'rgba(201,168,76,0.06)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: '50%',
          width: 28, height: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 10, color: 'rgba(201,168,76,0.5)',
          fontFamily: 'monospace',
          transition: 'all 0.2s',
          flexShrink: 0,
        }}
      >
        ∿
      </button>
      {/* Barzakh note */}
      {revealed && note && (
        <div style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 72,
          background: 'rgba(6,13,18,0.95)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 6,
          padding: '10px 14px',
          maxWidth: 280,
          width: 'max-content',
          zIndex: 10,
          fontSize: 11,
          color: 'rgba(212,197,169,0.7)',
          fontStyle: 'italic',
          lineHeight: 1.6,
          textAlign: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
            بَرْزَخ — BARZAKH
          </div>
          "{note}"
          <button
            onClick={(e) => { e.stopPropagation(); setRevealed(false); }}
            style={{ display: 'block', margin: '8px auto 0', background: 'none', border: 'none', color: 'rgba(201,168,76,0.4)', cursor: 'pointer', fontSize: 10 }}
          >
            ×
          </button>
        </div>
      )}
      {/* Vertical line below */}
      <div style={{
        width: 1,
        height: 32,
        background: 'linear-gradient(180deg, rgba(201,168,76,0.05) 0%, rgba(201,168,76,0.3) 100%)',
      }} />
    </div>
  );
}

// ─── TRAIL STEP ───────────────────────────────────────────────────────────────

function TrailStepBlock({ step, stepIndex }: { step: TrailStep; stepIndex: number }) {
  const [sectionExpanded, setSectionExpanded] = useState(stepIndex === 0);

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Letter section header */}
      <button
        onClick={() => setSectionExpanded(v => !v)}
        style={{
          width: '100%',
          background: sectionExpanded ? 'rgba(201,168,76,0.06)' : 'rgba(6,13,18,0.5)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 8,
          padding: '14px 18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          textAlign: 'left',
          transition: 'background 0.2s',
        }}
      >
        {/* Step number */}
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(201,168,76,0.1)',
          border: '1px solid rgba(201,168,76,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, color: '#c9a84c', fontFamily: 'monospace', fontWeight: 700,
          flexShrink: 0,
        }}>
          {String(stepIndex + 1).padStart(2, '0')}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 4 }}>
            {step.letterSection}
          </div>
          <div style={{ fontSize: 12, color: '#d4c5a9', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 6 }}>
            {step.letterPhrase}
          </div>
          <div style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.5 }}>
            {step.letterContext}
          </div>
        </div>

        <div style={{
          color: '#c9a84c', fontSize: 14, flexShrink: 0,
          transform: sectionExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>+</div>
      </button>

      {/* Expanded nodes */}
      {sectionExpanded && (
        <div style={{
          marginTop: 8,
          paddingLeft: 20,
          borderLeft: '1px solid rgba(201,168,76,0.1)',
          marginLeft: 14,
          animation: 'trailExpand 0.25s ease',
        }}>
          {/* Platform nodes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            {step.nodes.map((node, i) => (
              <React.Fragment key={node.id}>
                <TrailNodeCard node={node} index={i} />
                {i < step.nodes.length - 1 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px',
                  }}>
                    <div style={{ flex: 1, height: 1, background: 'rgba(201,168,76,0.08)' }} />
                    <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.3)', fontFamily: 'monospace' }}>manifests as</div>
                    <div style={{ flex: 1, height: 1, background: 'rgba(201,168,76,0.08)' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Letter section link */}
          <Link
            href={`/open-letter#section-${step.letterSectionId}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 9, color: 'rgba(201,168,76,0.5)', textDecoration: 'none',
              fontFamily: 'monospace', letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '5px 10px',
              border: '1px solid rgba(201,168,76,0.12)',
              borderRadius: 4,
              marginBottom: 4,
              transition: 'color 0.2s',
            }}
          >
            ← Return to letter section
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export function GuidedPathTrail() {
  const [activeFilter, setActiveFilter] = useState<'all' | 'garden' | 'irl' | 'mrg'>('all');
  const [trailMode, setTrailMode] = useState<'sequential' | 'map'>('sequential');

  const filteredSteps = TRAIL_STEPS.map(step => ({
    ...step,
    nodes: activeFilter === 'all'
      ? step.nodes
      : step.nodes.filter(n => n.type === activeFilter || n.type === 'letter'),
  })).filter(step => step.nodes.length > 0);

  return (
    <div style={{
      background: 'rgba(6,13,18,0.6)',
      border: '1px solid rgba(201,168,76,0.15)',
      borderRadius: 12,
      overflow: 'hidden',
      margin: '32px 0',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'rgba(201,168,76,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 8 }}>
              ◈ GUIDED PATH TRAIL
            </div>
            <h3 style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: '#d4c5a9', marginBottom: 6 }}>
              How the Letter Manifests in the Platform
            </h3>
            <p style={{ fontSize: 12, color: '#7a6f5d', lineHeight: 1.6, maxWidth: 480 }}>
              Each section of the Open Letter seeds a module, an encounter, or a resource. Follow the trail to see how the letter becomes the garden — how the founder's walk becomes your walk.
            </p>
          </div>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {(['sequential', 'map'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setTrailMode(mode)}
                style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '6px 12px', borderRadius: 4,
                  background: trailMode === mode ? 'rgba(201,168,76,0.12)' : 'transparent',
                  border: `1px solid ${trailMode === mode ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
                  color: trailMode === mode ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {mode === 'sequential' ? '⌇ Trail' : '◈ Map'}
              </button>
            ))}
          </div>
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
          {(['all', 'garden', 'irl', 'mrg'] as const).map(f => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              style={{
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '5px 12px', borderRadius: 20,
                background: activeFilter === f ? `${TYPE_COLORS[f] || '#c9a84c'}15` : 'transparent',
                border: `1px solid ${activeFilter === f ? (TYPE_COLORS[f] || '#c9a84c') + '50' : 'rgba(201,168,76,0.12)'}`,
                color: activeFilter === f ? (TYPE_COLORS[f] || '#c9a84c') : 'rgba(212,197,169,0.4)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {f === 'all' ? 'All Modules' : TYPE_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      {/* Trail content */}
      <div style={{ padding: '20px 24px' }}>
        {trailMode === 'sequential' ? (
          <div>
            {filteredSteps.map((step, i) => (
              <React.Fragment key={step.letterSectionId}>
                <TrailStepBlock step={step} stepIndex={i} />
                {i < filteredSteps.length - 1 && (
                  <BarzakhConnector note={step.barzakhNote} />
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          /* Map view — visual grid */
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 12,
            }}>
              {TRAIL_STEPS.flatMap(step =>
                step.nodes.map(node => (
                  <Link
                    key={node.id}
                    href={node.href}
                    style={{
                      display: 'block',
                      background: `${node.color}08`,
                      border: `1px solid ${node.color}25`,
                      borderTop: `3px solid ${node.color}`,
                      borderRadius: 8,
                      padding: '14px 16px',
                      textDecoration: 'none',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{ fontSize: 8, color: node.color, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                      {node.icon} {TYPE_LABELS[node.type]}
                    </div>
                    <div style={{ fontSize: 12, color: '#d4c5a9', fontWeight: 600, marginBottom: 4 }}>
                      {node.label}
                    </div>
                    <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.5 }}>
                      {node.phrase}
                    </div>
                    {node.arabic && (
                      <div style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${node.color}60`, direction: 'rtl', marginTop: 8 }}>
                        {node.arabic}
                      </div>
                    )}
                  </Link>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '14px 24px',
        borderTop: '1px solid rgba(201,168,76,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
      }}>
        <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.35)', fontStyle: 'italic', fontFamily: 'monospace' }}>
          "I'd like to walk with you on a journey processing what I see." — Open Letter
        </p>
        <Link
          href="/open-letter"
          style={{
            fontSize: 9, color: '#c9a84c', textDecoration: 'none',
            fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase',
            border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4, padding: '5px 10px',
          }}
        >
          Read the full letter →
        </Link>
      </div>
    </div>
  );
}
